import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Check, X, MessageSquare, FileText, Video, ExternalLink, ChevronRight, ShieldCheck } from 'lucide-react';
import api from '../../lib/api';
import { Invitation } from '../../types';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Textarea from '../../components/ui/Textarea';
import { formatDate, cn, isValidHttpHttpsUrl } from '../../lib/utils';
import FileUploader from '../../components/ui/FileUploader';
import { toSlug } from '../../lib/slug';
import SignContractModal, { SignaturePayload } from '../../components/SignContractModal';
import { useAuthStore } from '../../stores/authStore';
import InvitationNextSteps from '../../components/kol/InvitationNextSteps';

type TabType = 'details' | 'scripts' | 'submissions';

export default function KOLInvitationDetail() {
  const { id: rawId } = useParams();
  const routeParam = rawId ? decodeURIComponent(rawId) : '';
  const navigate = useNavigate();
  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [responding, setResponding] = useState(false);
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [showModificationDialog, setShowModificationDialog] = useState(false);
  const [modificationRequest, setModificationRequest] = useState('');

  const [scripts, setScripts] = useState<any[]>([]);

  // Submission state
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [captionText, setCaptionText] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState(''); // YouTube/Drive link
  const [submittingContent, setSubmittingContent] = useState(false);

  type InvitationContractRow = {
    id: string;
    title: string;
    renderedBody: string;
    createdAt: string;
    kolAcceptedAt?: string | null;
    signedAt?: string | null;
    signerName?: string | null;
    signatureType?: string | null;
    template?: { id: string; name: string } | null;
  };
  const [contracts, setContracts] = useState<InvitationContractRow[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [signModalContract, setSignModalContract] = useState<InvitationContractRow | null>(null);
  const { user } = useAuthStore();

  useEffect(() => {
    if (!routeParam) return;
    void loadInvitation();
  }, [routeParam]);

  useEffect(() => {
    if (!invitation?.id) return;
    void loadScripts();
    void loadSubmissions();
    void loadContracts();
  }, [invitation?.id]);

  /** Prefer clean `/kol/invitations/:urlSlug` in the address bar once loaded. */
  useEffect(() => {
    if (!invitation?.urlSlug || !routeParam) return;
    if (routeParam !== invitation.urlSlug) {
      navigate(`/kol/invitations/${encodeURIComponent(invitation.urlSlug)}`, { replace: true });
    }
  }, [invitation?.urlSlug, routeParam, navigate]);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/invitations/${encodeURIComponent(routeParam)}`);
      setInvitation(response.data);
    } catch (error) {
      console.error('Failed to load invitation:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadScripts = async () => {
    if (!invitation?.id) return;
    try {
      const response = await api.get('/scripts', { params: { invitationId: invitation.id } });
      setScripts(response.data);
    } catch (error) {
      console.error('Failed to load scripts:', error);
    }
  };

  const loadSubmissions = async () => {
    if (!invitation?.id) return;
    try {
      const response = await api.get('/submissions', { params: { invitationId: invitation.id } });
      setSubmissions(response.data);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    }
  };

  const loadContracts = async () => {
    if (!invitation?.id) return;
    try {
      setContractsLoading(true);
      const response = await api.get(`/invitations/${encodeURIComponent(invitation.urlSlug || invitation.id)}/contracts`);
      setContracts(response.data);
    } catch (error) {
      console.error('Failed to load contracts:', error);
    } finally {
      setContractsLoading(false);
    }
  };

  const downloadContractPdf = async (contractId: string) => {
    const res = await api.get(`/contracts/${contractId}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `contract-${contractId.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitSignature = async (contractId: string, payload: SignaturePayload) => {
    if (!invitation?.id) return;
    try {
      setAcceptingId(contractId);
      await api.post(`/invitations/${encodeURIComponent(invitation.urlSlug || invitation.id)}/contracts/${contractId}/accept`, payload);
      setSignModalContract(null);
      await loadContracts();
    } finally {
      setAcceptingId(null);
    }
  };

  const handleRespond = async (status: 'ACCEPTED' | 'REJECTED' | 'MODIFICATION_REQUESTED') => {
    // For modification requests, show dialog first
    if (status === 'MODIFICATION_REQUESTED') {
      setShowModificationDialog(true);
      return;
    }

    if (!invitation?.id) return;
    try {
      setResponding(true);
      await api.post(`/invitations/${encodeURIComponent(invitation.urlSlug || invitation.id)}/respond`, { status });
      await loadInvitation();
      setNotice({
        type: 'success',
        message: status === 'ACCEPTED' ? 'Invitation accepted.' : 'Invitation declined.',
      });
    } catch (error: any) {
      console.error('Failed to respond:', error);
      const errorMsg = error.response?.data?.error || 'Failed to respond to invitation';
      setNotice({ type: 'error', message: errorMsg });
    } finally {
      setResponding(false);
    }
  };

  // Any contract that is neither signed nor (legacy) acknowledged counts as outstanding
  const hasUnacknowledged = contracts.some((c) => !c.signedAt && !c.kolAcceptedAt);

  const handleSubmitModificationRequest = async () => {
    if (!invitation?.id) return;
    if (!modificationRequest.trim()) {
      setNotice({ type: 'error', message: 'Please describe what changes you would like to request' });
      return;
    }

    try {
      setResponding(true);
      // Update status to MODIFICATION_REQUESTED
      await api.post(`/invitations/${encodeURIComponent(invitation.urlSlug || invitation.id)}/respond`, { status: 'MODIFICATION_REQUESTED' });
      // Send the modification request as a message
      await api.post(`/invitations/${encodeURIComponent(invitation.urlSlug || invitation.id)}/messages`, { body: modificationRequest });
      await loadInvitation();
      setShowModificationDialog(false);
      setModificationRequest('');
      setNotice({ type: 'success', message: 'Modification request sent.' });
    } catch (error) {
      console.error('Failed to send modification request:', error);
      setNotice({ type: 'error', message: 'Failed to send modification request' });
    } finally {
      setResponding(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    
    if (!invitation?.id) return;
    try {
      setSending(true);
      await api.post(`/invitations/${encodeURIComponent(invitation.urlSlug || invitation.id)}/messages`, { body: messageText });
      setMessageText('');
      await loadInvitation();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  // Submission handlers
  // File upload handler - currently using YouTube/Drive links instead
  // Commented out as file upload is optional and handled inline
  // const handleFileUpload = (filePath: string) => {
  //   setUploadedFiles([...uploadedFiles, filePath]);
  // };

  const handleSubmitContent = async () => {
    if (!invitation?.id) return;
    const link = videoUrl.trim();
    if (uploadedFiles.length === 0 && !link) {
      setNotice({ type: 'error', message: 'Please either upload files or provide a YouTube/Drive link' });
      return;
    }
    if (link && !isValidHttpHttpsUrl(link)) {
      setNotice({ type: 'error', message: 'Enter a valid YouTube or Google Drive link starting with https://' });
      return;
    }

    const media = [
      ...uploadedFiles.map((filePath) => ({
        type: /\.(mp4|mov|avi|webm)$/i.test(filePath) ? 'VIDEO' : 'IMAGE',
        url: filePath,
      })),
      ...(link ? [{ type: 'VIDEO', url: link, title: 'External Video Link' }] : []),
    ];

    try {
      setSubmittingContent(true);
      await api.post('/submissions', {
        invitationId: invitation.id,
        captionText,
        media,
      });

      setCaptionText('');
      setUploadedFiles([]);
      setVideoUrl('');
      await loadSubmissions();
      setNotice({ type: 'success', message: 'Content submitted.' });
    } catch (error: unknown) {
      console.error('Failed to submit content:', error);
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      setNotice({ type: 'error', message: msg || 'Failed to submit content' });
    } finally {
      setSubmittingContent(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12">Loading...</div>;
  }

  if (!invitation || !invitation.job) {
    return <div className="text-center py-12 text-gray-500">Invitation not found</div>;
  }

  const job = invitation.job;
  const isPending = invitation.status === 'PENDING';
  const isAccepted = invitation.status === 'ACCEPTED';
  const latestScript = scripts.length > 0 ? scripts[0] : null;
  const isScriptApproved = latestScript?.status === 'APPROVED_BY_BRAND';

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-gray-500">
        <button onClick={() => navigate('/kol/invitations')} className="hover:text-green-600 transition-colors">Invitations</button>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-medium truncate max-w-md">{job.title}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          
              <Button variant="ghost" size="icon" onClick={() => navigate('/kol/invitations')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">{job.title}</h1>
            <p className="text-gray-600 mt-1">{job.brandOrg?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={invitation.status === 'ACCEPTED' ? 'success' : 'secondary'} title={
            invitation.status === 'PENDING' ? 'Awaiting your response to this invitation.' :
            invitation.status === 'ACCEPTED' ? 'You have accepted this invitation.' :
            invitation.status === 'REJECTED' ? 'You have declined this invitation.' :
            invitation.status === 'MODIFICATION_REQUESTED' ? 'You have requested changes to the terms.' :
            ''
          }>
            {invitation.status}
          </Badge>
          {invitation.isShortlisted && <Badge variant="success" title="You've been shortlisted by the agency for this campaign.">Shortlisted</Badge>}
          {invitation.isApprovedForProduction && (
              <Badge variant="success" title="The brand approved your shortlist — you can proceed with scripts and content for this campaign.">
                Shortlisting approved
              </Badge>
            )}
        </div>
      </div>

      {notice && (
        <div
          role="status"
          className={`rounded-xl border px-4 py-3 text-sm ${
            notice.type === 'success'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200'
              : 'border-rose-500/30 bg-rose-500/10 text-rose-800 dark:text-rose-200'
          }`}
        >
          {notice.message}
        </div>
      )}

      <InvitationNextSteps
        status={invitation.status}
        hasUnsignedContracts={hasUnacknowledged}
        scriptApproved={isScriptApproved}
        hasSubmission={submissions.length > 0}
      />

      {/* Action Buttons */}
      {isPending && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-700 mb-4">
                {hasUnacknowledged
                  ? 'Sign the contract(s) below first. Accept stays locked until every linked contract is signed.'
                  : 'You have been invited to this campaign. Review the brief, then respond.'}
              </p>
            <div className="flex gap-3">
              
              <Button
                onClick={() => handleRespond('ACCEPTED')}
                disabled={responding || hasUnacknowledged}
                className={`flex items-center gap-2 ${hasUnacknowledged ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Check className="w-4 h-4" />
                Accept Invitation
              </Button>
              
              <Button
                variant="outline"
                onClick={() => handleRespond('MODIFICATION_REQUESTED')}
                disabled={responding}
              >
                Request Changes
              </Button>
              
              <Button
                variant="outline"
                onClick={() => handleRespond('REJECTED')}
                disabled={responding}
              >
                <X className="w-4 h-4" />
                Decline
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Contracts for this invitation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-gray-600">
            When the agency links a contract to your invitation, it appears here. Sign it here first,
            then accept the invitation.
          </p>
          {contractsLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : contracts.length === 0 ? (
            <p className="text-sm text-gray-500">No contract linked to this invitation yet.</p>
          ) : (
            <ul className="space-y-3">
              {contracts.map((c) => {
                const signed = !!c.signedAt;
                const legacyAck = !signed && !!c.kolAcceptedAt;
                return (
                  <li
                    key={c.id}
                    className="flex flex-col gap-2 border rounded-lg p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        {c.title}
                        {signed && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                            <ShieldCheck size={12} /> Signed
                          </span>
                        )}
                        {!signed && !legacyAck && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                            Pending signature
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {c.template?.name || 'Custom'} · {new Date(c.createdAt).toLocaleString()}
                        {signed && (
                          <span className="text-emerald-700">
                            {' '}· Signed by {c.signerName || 'you'} on{' '}
                            {new Date(c.signedAt!).toLocaleString()}
                          </span>
                        )}
                        {legacyAck && (
                          <span className="text-green-700">
                            {' '}· You acknowledged {new Date(c.kolAcceptedAt!).toLocaleString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => downloadContractPdf(c.id)}
                      >
                        Download PDF
                      </Button>
                      {!signed && !legacyAck && invitation.status !== 'REJECTED' && invitation.status !== 'WITHDRAWN' && (
                        <Button
                          type="button"
                          size="sm"
                          disabled={!!acceptingId}
                          onClick={() => setSignModalContract(c)}
                          className="bg-emerald-600 hover:bg-emerald-700 border-emerald-600"
                        >
                          <ShieldCheck size={14} className="mr-1" />
                          Sign contract
                        </Button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {signModalContract && (
        <SignContractModal
          isOpen={!!signModalContract}
          onClose={() => setSignModalContract(null)}
          onSign={(payload) => submitSignature(signModalContract.id, payload)}
          contractTitle={signModalContract.title}
          contractBody={signModalContract.renderedBody || ''}
          defaultSignerName={user?.fullName || ''}
          defaultSignerEmail={user?.email || ''}
          submitting={acceptingId === signModalContract.id}
        />
      )}

      {/* What's Next Banner */}
      {invitation.status === 'ACCEPTED' && !invitation.isShortlisted && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm font-medium text-green-900">Your acceptance has been noted!</p>
          <p className="text-xs text-green-700 mt-1">The agency will shortlist you for production. You'll be notified when approved.</p>
        </div>
      )}
      {invitation.isShortlisted && !invitation.isApprovedForProduction && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm font-medium text-blue-900">You've been shortlisted!</p>
          <p className="text-xs text-blue-700 mt-1">The brand will review and approve your shortlist. Hang tight!</p>
        </div>
      )}
      {invitation.isApprovedForProduction && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <p className="text-sm font-medium text-emerald-900">Shortlisting approved!</p>
          <p className="text-xs text-emerald-700 mt-1">Submit your script draft in the Scripts tab, then proceed to content submission once approved.</p>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('details')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'details'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Job Details
          </button>
          {isAccepted && (
            <>
              <button
                onClick={() => setActiveTab('scripts')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === 'scripts'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-4 h-4" />
                Scripts
              </button>
              <button
                onClick={() => setActiveTab('submissions')}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === 'submissions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Video className="w-4 h-4" />
                Submissions
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'details' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Job Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{job.description}</p>
                </div>

                {job.demoVideoLink && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Demo Video</h4>
                    <a href={job.demoVideoLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {job.demoVideoLink}
                    </a>
                  </div>
                )}

                {job.exampleScript && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Example Script (To be discussed in Script Studio)</h4>
                    <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded">{job.exampleScript}</p>
                  </div>
                )}

                {job.products && job.products.length > 0 && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Products</h4>
                    <div className="space-y-2">
                      {job.products.map((product) => (
                        <div key={product.id} className="p-3 border rounded">
                          <div className="font-medium">{product.title}</div>
                          {product.description && (
                            <p className="text-sm text-gray-600 mt-1">{product.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Messages */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Messages
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {invitation.messages && invitation.messages.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {invitation.messages.map((message) => (
                      <div key={message.id} className="p-3 bg-gray-50 rounded">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-900">
                            {message.senderUser?.fullName || message.senderUser?.email || 'Anonymous'}
                          </span>
                          <span className="text-xs text-gray-500">{formatDate(message.createdAt)}</span>
                        </div>
                        <p className="text-sm text-gray-700">{message.body}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No messages yet</p>
                )}

                <div className="space-y-2">
                  <Textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type your message..."
                    rows={3}
                  />
                  
              <Button onClick={handleSendMessage} disabled={sending || !messageText.trim()}>
                    Send Message
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Rewards</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {job.rewardsJson && (
                  <>
                    {(job.rewardsJson as any).cash > 0 && (
                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="text-sm text-gray-600">Cash</div>
                        <div className="text-xl font-bold text-green-600">RM {(job.rewardsJson as any).cash}</div>
                      </div>
                    )}
                    {(job.rewardsJson as any).product && (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="text-sm text-gray-600">Product</div>
                        <div className="font-semibold text-blue-600">{(job.rewardsJson as any).product}</div>
                      </div>
                    )}
                    {(job.rewardsJson as any).commission > 0 && (
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <div className="text-sm text-gray-600">Commission</div>
                        <div className="text-xl font-bold text-purple-600">{(job.rewardsJson as any).commission}%</div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {job.contractStart && (
                  <div>
                    <div className="text-sm text-gray-600">Start Date</div>
                    <div className="font-medium">{formatDate(job.contractStart)}</div>
                  </div>
                )}
                {job.contractEnd && (
                  <div>
                    <div className="text-sm text-gray-600">End Date</div>
                    <div className="font-medium">{formatDate(job.contractEnd)}</div>
                  </div>
                )}
                <div>
                  <div className="text-sm text-gray-600">Invitation Sent</div>
                  <div className="font-medium">{formatDate(invitation.createdAt)}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Scripts Tab — deep-link to Script Studio (single draft per invitation in practice) */}
      {activeTab === 'scripts' && isAccepted && (
        <div className="space-y-6">
          {!invitation.isApprovedForProduction ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              You need shortlisting approval from the brand before you can work on scripts. Please wait for the agency to
              shortlist and the brand to approve you.
            </div>
          ) : (
            <Card className="border-[#01696f]/30 bg-gradient-to-br from-[#e6f4f5]/50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#01696f]">
                  <FileText className="w-5 h-5" />
                  Script studio
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-gray-600">
                  Drafting, approvals, and version history for this campaign happen in{' '}
                  <span className="font-medium text-gray-900">Script studio</span> — the same experience agency and brand
                  use, tailored for your role.
                </p>
                {latestScript ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <Badge variant="outline" className="border-[#01696f]/40 text-[#01696f]">
                      v{latestScript.version} · {latestScript.status.replace(/_/g, ' ')}
                    </Badge>
                    <Link
                      to={`/kol/scripts/${encodeURIComponent(toSlug(job.title, latestScript.id))}`}
                      className={cn(
                        'inline-flex items-center justify-center text-sm font-medium transition-all focus-visible:outline-none h-10 px-4 py-2 rounded-lg text-white',
                        'bg-[#01696f] hover:bg-[#015a5f]'
                      )}
                    >
                      Open script studio
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-700">
                    <p className="mb-3 text-gray-600">
                      Your agency hasn&apos;t attached a script draft to this invitation yet. When it&apos;s ready,
                      you&apos;ll open it here.
                    </p>
                    <Link
                      to="/kol/scripts"
                      className="inline-flex items-center justify-center text-sm font-medium transition-all focus-visible:outline-none h-10 px-4 py-2 rounded-lg btn-glass"
                    >
                      View all my scripts
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Submissions Tab */}
      {activeTab === 'submissions' && isAccepted && (
        <div className="space-y-6">
          {/* Upload Content */}
          {isScriptApproved ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Video className="w-5 h-5" />
                  Submit Content
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Caption (Optional)</label>
                  <Textarea
                    value={captionText}
                    onChange={(e) => setCaptionText(e.target.value)}
                    placeholder="Add a caption for your content..."
                    rows={3}
                  />
                </div>

                {/* YouTube/Drive Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video Link (YouTube/Google Drive) - Recommended
                  </label>
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=... or https://drive.google.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    💡 Upload your video to YouTube or Google Drive and paste the link here for better performance
                  </p>
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">OR</span>
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Videos/Images (Optional)
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Accepted formats: MP4, MOV, JPG, PNG. Max file size: 50MB per file.
                  </p>
                  <FileUploader
                    onUpload={(urls) => setUploadedFiles([...uploadedFiles, ...urls])}
                    accept="video/*,image/*"
                    multiple={true}
                  />
                  
                  {uploadedFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-sm text-gray-600">Uploaded files:</p>
                      {uploadedFiles.map((file, idx) => (
                        <div key={idx} className="text-sm bg-green-50 p-2 rounded flex items-center justify-between">
                          <span className="truncate">{file}</span>
                          <button
                            onClick={() => setUploadedFiles(uploadedFiles.filter((_, i) => i !== idx))}
                            className="text-red-600 hover:text-red-700 ml-2"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                
              <Button
                  onClick={handleSubmitContent}
                  disabled={submittingContent || (uploadedFiles.length === 0 && !videoUrl.trim())}
                >
                  Submit Content
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="pt-6">
                <p className="text-sm text-yellow-900">
                  You must have an approved script before submitting content.
                  Please create and submit a script in the Scripts tab first.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Submissions List */}
          <Card>
            <CardHeader>
              <CardTitle>Your Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              {submissions.length > 0 ? (
                <div className="space-y-4">
                  {submissions.map((submission) => (
                    <div key={submission.id} className="border rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Version {submission.version}</span>
                          <Badge
                            variant={
                              submission.status === 'APPROVED'
                                ? 'success'
                                : submission.status === 'REVISION_REQUESTED'
                                ? 'destructive'
                                : 'secondary'
                            }
                          >
                            {submission.status}
                          </Badge>
                        </div>
                        <span className="text-sm text-gray-500">{formatDate(submission.createdAt)}</span>
                      </div>

                      {submission.captionText && (
                        <p className="text-sm text-gray-700">{submission.captionText}</p>
                      )}

                      {submission.media && submission.media.length > 0 && (
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-gray-700">Media:</h4>
                          <div className="grid grid-cols-1 gap-2">
                            {submission.media.map((media: any) => {
                              const isExternalLink = media.url.startsWith('http://') || media.url.startsWith('https://');
                              const isYouTube = media.url.includes('youtube.com') || media.url.includes('youtu.be');
                              const isDrive = media.url.includes('drive.google.com');
                              
                              return (
                                <div key={media.id} className="border rounded p-3 bg-gray-50">
                                  <a
                                    href={media.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:underline flex items-center gap-2"
                                  >
                                    {isYouTube ? (
                                      <>
                                        <Video className="w-5 h-5 text-red-600" />
                                        <span>YouTube Video</span>
                                      </>
                                    ) : isDrive ? (
                                      <>
                                        <ExternalLink className="w-5 h-5 text-blue-600" />
                                        <span>Google Drive File</span>
                                      </>
                                    ) : media.type === 'VIDEO' ? (
                                      <>
                                        <Video className="w-5 h-5 text-purple-600" />
                                        <span>Video {isExternalLink ? 'Link' : 'File'}</span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="text-xl">🖼️</span>
                                        <span>Image {isExternalLink ? 'Link' : 'File'}</span>
                                      </>
                                    )}
                                    <ExternalLink className="w-3 h-3 ml-auto" />
                                  </a>
                                  {media.title && (
                                    <p className="text-xs text-gray-500 mt-1">{media.title}</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {submission.revisionComments && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                          <div className="text-sm font-medium text-yellow-900 mb-1">Revision Comments:</div>
                          <p className="text-sm text-yellow-800">{submission.revisionComments}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-8 text-gray-500">No submissions yet.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modification Request Dialog */}
      {showModificationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Request Modification
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Please describe what changes you would like to request for this job invitation. 
                The agency will review your request and respond accordingly.
              </p>
              <Textarea
                value={modificationRequest}
                onChange={(e) => setModificationRequest(e.target.value)}
                placeholder="e.g., I would like to adjust the deliverable timeline, or I need more information about the product..."
                rows={6}
                className="w-full"
              />
              <div className="flex gap-3 mt-6">
                
              <Button
                  onClick={handleSubmitModificationRequest}
                  disabled={responding || !modificationRequest.trim()}
                  className="flex-1"
                >
                  {responding ? 'Sending...' : 'Send Request'}
                </Button>
                
              <Button
                  variant="outline"
                  onClick={() => {
                    setShowModificationDialog(false);
                    setModificationRequest('');
                  }}
                  disabled={responding}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

